import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveComponent as _resolveComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, resolveDirective as _resolveDirective, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = ["href"]
const _hoisted_2 = ["src", "alt"]

import {
  reactive,
  ref,
  computed,
  PropType, ComputedRef, onMounted, nextTick, inject,
} from 'vue';
import { useI18n } from 'vue-i18n';

import { rules } from '@/entities/volta-measure/components/form/common';

import { getDomainPath } from '@/services';
import { useFormUpdate } from '@/hooks/forms';
import { useForm } from '@/core/hooks/forms';
import { useFormErrorMessageHandler } from '@/utils/form';

import { useGetInspectionGroupQuery } from '@/entities/inspection-group/hooks';
import { useGetInspectionQuery } from '@/entities/inspection/hooks';
import {
  useUpdateVoltaMeasureMutation,
  useGetVoltaMeasureQuery, useDeleteVoltaMeasureMutation,
  useUploadUpdateVoltaMeasure
} from '@/entities/volta-measure/hooks';

import { UpdateVoltaMeasureData, VoltaMeasure } from '@/entities/volta-measure/types';
import { FilePondOptions } from 'filepond';

import {
  NkForm,
  NkFormItem,
  NkGroup,
  NkButton,
  NkInputNumber,
  NkInputText,
  NkGrid,
  NkCol,
  NkGallery,
  NkText,
  NkFilePond,
  NkTextArea, NkSelect, NkOption,
} from '@/core/components';

import AnnotationSeveritySelect
  from '@/entities/annotation-severity/components/inputs/AnnotationSeveritySelect.vue';
import { ServiceInject, serviceInjectionKey } from '@/entities/services/keys';
import { useGetVoltaItemQuery, useGetVoltaItemsQuery } from '@/entities/volta-item/hooks';
import { requiredIf } from '@vuelidate/validators';


export default /*#__PURE__*/_defineComponent({
  __name: 'VoltaMeasureUpdateForm',
  props: {
  voltaMeasure: {
    type: Number as PropType<VoltaMeasure['id']>,
    required: true,
  },
},
  emits: ['submitted'],
  setup(__props, { emit }) {

const props = __props


const { onSubmitted } = useForm(emit);
const { locale } = useI18n();



const filePond = ref<typeof NkFilePond>()

const form = ref();
const fields = reactive({
  inspection: 0,
  measureValue: 0,
  amperage: null,
  voltage: null,
  severity: 0,
  positionFromHub: 0,
  comment: null,
  file: '',
  voltaItem: 0,
} as UpdateVoltaMeasureData & { comment: string | null });

const queryData = computed(() => ({ id: props.voltaMeasure }));

const {
  query: {
    data,
  },
  mutation: {
    mutateAsync,
    isLoading,
    isError,
    error: updateVoltaMeasureError,
  },
} = useFormUpdate({
  fields,
  queryData,
  mutationHook: useUpdateVoltaMeasureMutation,
  queryHook: useGetVoltaMeasureQuery,
});

const { error } = useFormErrorMessageHandler(updateVoltaMeasureError);

const {
  mutateAsync: deleteMeasure,
  isLoading: deleteIsLoading,
  isError: isDeleteError,
  error: deleteVoltaMeasureError,
} = useDeleteVoltaMeasureMutation();

const {
  mutateAsync: uploadImage
} = useUploadUpdateVoltaMeasure()

const { error: deleteError } = useFormErrorMessageHandler(deleteVoltaMeasureError);

const inspectionQueryData: ComputedRef = computed(() => ({ id: data.value?.inspection }));
const hasInspectionQueryData = computed(() => !!inspectionQueryData.value.id);
const { data: inspection } = useGetInspectionQuery(inspectionQueryData, { enabled: hasInspectionQueryData });

const inspectionGroupQueryData: ComputedRef = computed(() => ({ id: inspection.value?.inspectionGroup }));
const hasInspectionGroupQueryData = computed(() => !!inspectionGroupQueryData.value.id);
const { data: inspectionGroup } = useGetInspectionGroupQuery(inspectionGroupQueryData, { enabled: hasInspectionGroupQueryData });

const { isVoltaExpress, isVoltaPlus } = inject(serviceInjectionKey) as ServiceInject;

const voltaItemQueryData: ComputedRef = computed(() => ({ id: data.value?.voltaItem }));
const hasVoltaItemQueryData = computed(() => !!voltaItemQueryData.value.id);
const { data: originalVoltaItem } = useGetVoltaItemQuery(voltaItemQueryData, {enabled: hasVoltaItemQueryData});

const voltaItemsQueryFilters: ComputedRef = computed(() => ({ isBladeItem: originalVoltaItem.value?.isBladeItem }));
const hasVoltaItemsQueryData = computed(() => !!voltaItemsQueryFilters.value);
const { data: voltaItems, isLoading: isLoadingVoltaItems } = useGetVoltaItemsQuery({ enabled: hasVoltaItemsQueryData }, voltaItemsQueryFilters);
const currentVoltaItem = computed(() => voltaItems.value?.find(item => item.id === fields.voltaItem));

const _rules = computed(() => ({
  measureValue: { required: requiredIf(() => currentVoltaItem.value?.isMeasureRequired ?? true) },
  positionFromHub: { required: requiredIf(() => currentVoltaItem.value?.isPositionFromHubRequired ?? true) },
  severity: { required: requiredIf(() => currentVoltaItem.value?.isSeverityRequired ?? true) },
  amperage: { required: requiredIf(() => isVoltaPlus.value && (currentVoltaItem.value?.isMeasureRequired ?? true))},
  voltage: { required: requiredIf(() => isVoltaPlus.value && (currentVoltaItem.value?.isMeasureRequired ?? true))},
}));

fields.comment = data.value?.expertComment[locale.value];

const handleSubmit = () => {
  if (!isLoading.value) {
    if (fields.file) {
      const formData = new FormData()
      formData.append('file', fields.file)

      uploadImage({
        id: props.voltaMeasure,
        data: formData
      })
    }

    form.value.validator.$validate().then((valid: boolean) => {
      if (valid) {
        const voltaMeasureData = {
          ...fields,
          id: props.voltaMeasure,
        }

        if (fields.comment != null) {
          voltaMeasureData.expertComment = { 'en' : fields.comment, 'fr': fields.comment }
        }
        mutateAsync(voltaMeasureData).then((res) => {
          onSubmitted(res);
        });
      }
    });
  }
};

const handleDelete = () => {
  if (!deleteIsLoading.value) {
    deleteMeasure({ id: props.voltaMeasure }, {
      onSuccess: () => onSubmitted(),
    });
  }
};

onMounted(() => {
  nextTick(() => {
    filePond.value?.filePond._pond.setOptions({
            server: {
                process: null,
            }
        } as FilePondOptions)

    filePond.value.filePond._pond.on('addfile', (e, file) => {
          fields.file = file.file
    })

    filePond.value.filePond._pond.on('removefile', () => {
      fields.file = null
    })
  })
})

return (_ctx: any,_cache: any) => {
  const _component_ElAlert = _resolveComponent("ElAlert")!
  const _component_ElPopconfirm = _resolveComponent("ElPopconfirm")!
  const _directive_loading = _resolveDirective("loading")!

  return _withDirectives((_openBlock(), _createBlock(_unref(NkForm), {
    ref_key: "form",
    ref: form,
    fields: fields,
    rules: _unref(isVoltaExpress) ? _unref(rules) : _unref(_rules)
  }, {
    header: _withCtx(() => [
      (_unref(isError))
        ? (_openBlock(), _createBlock(_component_ElAlert, {
            key: 0,
            "show-icon": "",
            title: _unref(error),
            type: "error"
          }, null, 8, ["title"]))
        : _createCommentVNode("", true),
      (_unref(isDeleteError))
        ? (_openBlock(), _createBlock(_component_ElAlert, {
            key: 1,
            "show-icon": "",
            title: _unref(deleteError),
            type: "error"
          }, null, 8, ["title"]))
        : _createCommentVNode("", true)
    ]),
    footer: _withCtx(() => [
      _createVNode(_unref(NkGroup), {
        position: "right",
        spacing: 2
      }, {
        default: _withCtx(() => [
          (_ctx.$store.getters['connection/isGranted'](['volta_measure_delete']))
            ? (_openBlock(), _createBlock(_component_ElPopconfirm, {
                key: 0,
                title: _ctx.$t('actions.delete_confirm'),
                onConfirm: handleDelete
              }, {
                reference: _withCtx(() => [
                  _createVNode(_unref(NkButton), {
                    disabled: _unref(deleteIsLoading),
                    type: "danger"
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(_ctx.$t('actions.delete')), 1)
                    ]),
                    _: 1
                  }, 8, ["disabled"])
                ]),
                _: 1
              }, 8, ["title"]))
            : _createCommentVNode("", true),
          _createVNode(_unref(NkButton), {
            disabled: _unref(isLoading),
            type: "primary",
            onClick: handleSubmit
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$t('actions.update')), 1)
            ]),
            _: 1
          }, 8, ["disabled"])
        ]),
        _: 1
      })
    ]),
    default: _withCtx(() => [
      _createVNode(_unref(NkGrid), null, {
        default: _withCtx(() => [
          _createVNode(_unref(NkCol), { span: 12 }, {
            default: _withCtx(() => [
              _createVNode(_unref(NkFormItem), {
                prop: "voltaItem",
                label: _ctx.$t('volta-measure.form.item.label')
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(NkSelect), {
                    modelValue: fields.voltaItem,
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((fields.voltaItem) = $event)),
                    isDisabled: _unref(isLoadingVoltaItems)
                  }, {
                    default: _withCtx(() => [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(voltaItems), (voltaItem) => {
                        return (_openBlock(), _createBlock(_unref(NkOption), {
                          key: voltaItem.id,
                          value: voltaItem.id,
                          label: voltaItem.name[_unref(locale)]
                        }, {
                          default: _withCtx(() => [
                            _createTextVNode(_toDisplayString(voltaItem.name[_unref(locale)]), 1)
                          ]),
                          _: 2
                        }, 1032, ["value", "label"]))
                      }), 128))
                    ]),
                    _: 1
                  }, 8, ["modelValue", "isDisabled"])
                ]),
                _: 1
              }, 8, ["label"])
            ]),
            _: 1
          }),
          _createVNode(_unref(NkCol), { span: 12 }, {
            default: _withCtx(() => [
              _createVNode(_unref(NkFormItem), {
                prop: "positionFromHub",
                label: _ctx.$t('volta-measure.form.positionFromHub.label')
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(NkInputNumber), {
                    min: 0,
                    modelValue: fields.positionFromHub,
                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((fields.positionFromHub) = $event))
                  }, null, 8, ["modelValue"])
                ]),
                _: 1
              }, 8, ["label"])
            ]),
            _: 1
          }),
          _createVNode(_unref(NkCol), { span: 6 }, {
            default: _withCtx(() => [
              _createVNode(_unref(NkFormItem), {
                prop: "severity",
                label: _ctx.$t('misc.severity')
              }, {
                default: _withCtx(() => [
                  (_unref(inspectionGroup)?.service)
                    ? (_openBlock(), _createBlock(AnnotationSeveritySelect, {
                        key: 0,
                        modelValue: fields.severity,
                        "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((fields.severity) = $event)),
                        service: _unref(inspectionGroup)?.service
                      }, null, 8, ["modelValue", "service"]))
                    : _createCommentVNode("", true)
                ]),
                _: 1
              }, 8, ["label"])
            ]),
            _: 1
          }),
          _createVNode(_unref(NkCol), { span: 6 }, {
            default: _withCtx(() => [
              _createVNode(_unref(NkFormItem), {
                prop: "measureValue",
                label: _ctx.$t('volta-measure.form.measuredValue.label')
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(NkInputText), {
                    modelValue: fields.measureValue,
                    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((fields.measureValue) = $event))
                  }, null, 8, ["modelValue"])
                ]),
                _: 1
              }, 8, ["label"])
            ]),
            _: 1
          }),
          (_unref(isVoltaPlus))
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                _createVNode(_unref(NkCol), { span: 6 }, {
                  default: _withCtx(() => [
                    _createVNode(_unref(NkFormItem), {
                      prop: "amperage",
                      label: _ctx.$t('volta-measure.form.amperage.label')
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_unref(NkInputText), {
                          modelValue: fields.amperage,
                          "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((fields.amperage) = $event))
                        }, null, 8, ["modelValue"])
                      ]),
                      _: 1
                    }, 8, ["label"])
                  ]),
                  _: 1
                }),
                _createVNode(_unref(NkCol), { span: 6 }, {
                  default: _withCtx(() => [
                    _createVNode(_unref(NkFormItem), {
                      prop: "voltage",
                      label: _ctx.$t('volta-measure.form.voltage.label')
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_unref(NkInputText), {
                          modelValue: fields.voltage,
                          "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((fields.voltage) = $event))
                        }, null, 8, ["modelValue"])
                      ]),
                      _: 1
                    }, 8, ["label"])
                  ]),
                  _: 1
                })
              ], 64))
            : _createCommentVNode("", true),
          _createVNode(_unref(NkCol), { span: "12" }, {
            default: _withCtx(() => [
              _createVNode(_unref(NkFormItem), {
                label: _ctx.$t('misc.comment'),
                prop: "comment"
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(NkTextArea), {
                    modelValue: fields.comment,
                    "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((fields.comment) = $event))
                  }, null, 8, ["modelValue"])
                ]),
                _: 1
              }, 8, ["label"])
            ]),
            _: 1
          }),
          (!_unref(isVoltaExpress))
            ? (_openBlock(), _createBlock(_unref(NkCol), {
                key: 1,
                span: 12
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(NkFormItem), {
                    prop: "file",
                    label: _ctx.$t('misc.update-photo')
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_unref(NkFilePond), {
                        ref_key: "filePond",
                        ref: filePond
                      }, null, 512)
                    ]),
                    _: 1
                  }, 8, ["label"])
                ]),
                _: 1
              }))
            : _createCommentVNode("", true),
          (!_unref(isVoltaExpress))
            ? (_openBlock(), _createBlock(_unref(NkCol), {
                key: 2,
                span: 12
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(NkFormItem), null, {
                    default: _withCtx(() => [
                      (fields.uri)
                        ? (_openBlock(), _createBlock(_unref(NkGallery), { key: 0 }, {
                            default: _withCtx(() => [
                              _createElementVNode("a", {
                                class: "block h-full bg-black rounded-lg w-full",
                                href: _unref(getDomainPath)(fields.uri.split('/uploads/')[1])
                              }, [
                                _createElementVNode("img", {
                                  class: "h-full w-full object-contain",
                                  src: _unref(getDomainPath)(fields.uri.split('/uploads/')[1]),
                                  alt: `${ _ctx.$t('misc.measure') } : ${ fields.id }`
                                }, null, 8, _hoisted_2)
                              ], 8, _hoisted_1)
                            ]),
                            _: 1
                          }))
                        : (_openBlock(), _createBlock(_unref(NkText), {
                            key: 1,
                            type: "semibold-xxs"
                          }, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(_ctx.$t('volta-measure.form.file.no_file')), 1)
                            ]),
                            _: 1
                          }))
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }))
            : _createCommentVNode("", true)
        ]),
        _: 1
      })
    ]),
    _: 1
  }, 8, ["fields", "rules"])), [
    [_directive_loading, _unref(isLoading)]
  ])
}
}

})